/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

export const normalizePromptDateField = (value) => {
  if (value == null || value === '') {
    return null;
  }
  return String(value);
};

export const mapCatalogueRow = (row, dataContext = {}) => {
  const primaryKey = dataContext.primaryKey || 'promptRef';
  const versionKey = dataContext.versionKey || 'promptVersionRef';
  const promptRef = row?.[primaryKey] || row?.id || row?.promptRef || '';
  const promptVersionRef = row?.[versionKey] || row?.version || '';
  const versionCountRaw = row?.versionCount ?? row?.versionsCount;
  const versionCount =
    versionCountRaw != null && versionCountRaw !== '' && !Number.isNaN(Number(versionCountRaw))
      ? Number(versionCountRaw)
      : null;
  return {
    promptRef,
    promptVersionRef,
    name: row?.name || '',
    description: row?.description,
    comments: row?.comments || '',
    modifiedBy: row?.modifiedBy || '',
    updatedDate: normalizePromptDateField(row?.updatedDate),
    createdDate: normalizePromptDateField(row?.createdDate),
    versionCount,
    promptType: row?.promptType || row?.type || 'text'
  };
};

export const normalizeCatalogueRows = (responseData, dataContext = {}) => {
  const rows = Array.isArray(responseData) ? responseData : responseData?.data ?? responseData?.catalogue ?? [];
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.map((row) => mapCatalogueRow(row, dataContext)).filter((row) => row.promptRef);
};

export const normalizeCataloguePagination = (responseData) => {
  if (!responseData || Array.isArray(responseData)) {
    return null;
  }
  const { pagination } = responseData;
  if (!pagination) {
    return null;
  }
  return {
    offset: Number(pagination.offset) || 0,
    limit: Number(pagination.limit) || 50,
    totalCount: Number(pagination.totalCount) || 0,
    totalPages: Number(pagination.totalPages) || 0,
    currentPage: Number(pagination.currentPage) || 1
  };
};

export const filterCatalogueClientSide = (rows, query, searchFields = ['name', 'description', 'comments']) => {
  const trimmed = (query || '').trim().toLowerCase();
  if (!trimmed) {
    return rows;
  }
  return (rows || []).filter((row) =>
    searchFields.some((field) =>
      String(row?.[field] || '')
        .toLowerCase()
        .includes(trimmed)
    )
  );
};

export const normalizePromptVersionString = (value) => {
  const str = String(value ?? '').trim();
  if (!str) {
    return '';
  }
  return str.replace(/^[vV](?=\d)/, '');
};

/** Prefer promptVersionRef — API rows may carry a schema `version` that is not the prompt semver. */
export const resolvePromptVersionLabel = (row) => {
  if (row == null) {
    return '';
  }
  if (typeof row === 'string' || typeof row === 'number') {
    return normalizePromptVersionString(row);
  }
  return normalizePromptVersionString(row.promptVersionRef ?? row.version ?? '');
};

export const isLatestVersion = (selectedVersion, catalogueRow, latestVersionId = null) => {
  if (!selectedVersion) {
    return false;
  }
  const latest = latestVersionId || resolvePromptVersionLabel(catalogueRow);
  if (!latest) {
    return false;
  }
  return String(normalizePromptVersionString(selectedVersion)) === String(latest);
};

/**
 * Compare dotted prompt version strings (e.g. 1.0.9 vs 1.0.37) numerically per segment.
 * @returns {number} negative if a < b, positive if a > b, 0 if equal
 */
export const comparePromptVersionStrings = (a, b) => {
  const strA = normalizePromptVersionString(a);
  const strB = normalizePromptVersionString(b);
  if (strA === strB) {
    return 0;
  }
  if (!strA) {
    return -1;
  }
  if (!strB) {
    return 1;
  }

  const partsA = strA.split('.');
  const partsB = strB.split('.');
  const length = Math.max(partsA.length, partsB.length);

  for (let i = 0; i < length; i += 1) {
    const segA = partsA[i];
    const segB = partsB[i];
    if (segA === undefined) {
      return -1;
    }
    if (segB === undefined) {
      return 1;
    }

    const numA = Number(segA);
    const numB = Number(segB);
    if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
      if (numA !== numB) {
        return numA - numB;
      }
    } else {
      const cmp = segA.localeCompare(segB);
      if (cmp !== 0) {
        return cmp;
      }
    }
  }

  return 0;
};

export const resolveLatestVersionId = (versions = []) => {
  if (!Array.isArray(versions) || versions.length === 0) {
    return null;
  }
  const sorted = [...versions].sort((a, b) =>
    comparePromptVersionStrings(resolvePromptVersionLabel(b), resolvePromptVersionLabel(a))
  );
  return resolvePromptVersionLabel(sorted[0]) || null;
};
